import React, { useMemo, useRef, useState, useLayoutEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import apiClient from '../../api/client';
import { getKeystoneDescriptor, getKeystoneIconPath } from '../../data/keystoneCards';
import { buildDrugColorMap, normalizeOwnableSlug } from './skillTreeLayout';
import { normalizeSkillTreeDefinition } from '../SkillTreeMap';
import { useSkillTreeToast } from './SkillTreeToastContext';
import SkillTreeNodeGlyph from './SkillTreeNodeGlyph';
import DrugPairEffectPopover from './DrugPairEffectPopover';
import './SkillTreeOwnedCluster.css';

// Canonical sizing — mirrors ProgressMap NODE_R so the cluster reads as a
// scale-shrunken view of the main map. Keystones are ~1.43× the topic node
// radius on the map; we preserve that ratio in the cluster row too.
const NODE_R_TOPIC = 14;
const NODE_R_KEYSTONE = 20;

// Fallback color for an equipped drug whose node isn't found in the tree graph
// (off-tree / orphaned slug). The canonical "completed" green ProgressMap uses.
// Normal cards now take their SKILL-TREE IDENTITY color (resolveNodeIdentityColor
// via buildDrugColorMap) so the loadout reads as a color-coded mirror of the
// tree — gold card_unlock, keystone-affinity connectors, warfarin/psilocybin
// purple — instead of a flat green row. (Edward 2026-06-09 — "take the color
// they are on the skill tree; robustly associate it with the drug".)
const GREEN = '#22c55e';

// Inline-row cluster: a single horizontal row of compact atoms that sits
// right-of the PlayerTray orbs in the dashboard's bottom-left, with a
// reasonable margin. No label below atom (kept terse for the inline row);
// drug name shows in tooltip via <title>.
const NODE_PAD_X = 4;

async function fetchSkillTreeState() {
  const { data } = await apiClient.get('/api/skill-tree/player-state');
  return data;
}

// Drug-pair persistent max-HP effects, computed by the backend from the ACTIVE
// equipped slots. Surfaced as the Antidote / Toxicity pseudo nodes below. Anon /
// demo sessions 401 here -> no pairs -> no pseudo nodes (graceful).
async function fetchDrugPairs() {
  const { data } = await apiClient.get('/api/equipment/');
  return data?.drug_pairs || null;
}

function cellWidthFor(radius) {
  return radius * 2 + NODE_PAD_X * 2;
}

function prettifySlug(slug) {
  return String(slug || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

/**
 * `inline` — render the same atom row as a normal in-flow element instead of
 * the absolutely-positioned bottom-right overlay. Used by the mobile demo's
 * loadout strip (DrugLabMobileDemo) so mobile shows the TRUE hotbar nodes —
 * same query, colors, glyphs, and tap→SkillTreeNodeToast "tooltip" — rather
 * than a parallel chip rendering (Edward 2026-06-11). Skips the desktop
 * hot-picks alignment measurement, which is meaningless outside the dashboard
 * corner.
 */
export default function SkillTreeOwnedCluster({ inline = false, spotlight = false }) {
  // Player-state is the SINGLE source of truth for what's equipped. The equip
  // bar renders straight from `equipped_drugs` — the SAME authoritative set the
  // skill-tree nodes and combat read (status.py::_equipped_drug_slugs). Older
  // code intersected this with reservoir ownership (user_status==='golden'),
  // which silently hid an equipped-but-unowned card here while the skill tree
  // still showed it EQUIPPED → "2 in the tree, 1 in the equip bar". The invariant
  // is `equipped ⟹ owned` (enforced at equipment_service.equip_card); the only
  // way it breaks is a maintenance script writing equipment_state directly, i.e.
  // bad data, not a legitimate hide case. So we trust equipped_drugs and never
  // re-derive membership from ownership. Codex-reviewed 2026-06-04.
  const playerStateQuery = useQuery({
    queryKey: ['player', 'skill-tree'],
    queryFn: fetchSkillTreeState,
    staleTime: 30_000,
  });

  // The equip bar is view-only: CLICKING an atom opens its info as a centered
  // VIEWPORT modal tooltip (read-only — no Equip/Unequip/Switch). The loadout
  // strip shows what's equipped; modifying the loadout happens on the skill
  // tree, not here (Edward 2026-06-25). `readOnly:true` makes SkillTreeNodeToast
  // render the tooltip-only panel as a screen-centered modal. CLICK-only: a
  // hover-triggered centered modal flickers (the scrim covering the atom fires
  // mouseout, which closes the transient card), so the hotbar opens on click.
  const { openNodeToast, currentNodeId } = useSkillTreeToast();

  // Drug-pair player-level effects (Antidote / Toxicity pseudo nodes). Separate
  // query from player-state (which has no HP-pair data); react-query dedupes the
  // ['equipment'] key across the app so this is cheap.
  const drugPairsQuery = useQuery({
    queryKey: ['equipment', 'drug-pairs'],
    queryFn: fetchDrugPairs,
    staleTime: 30_000,
  });
  // The open pseudo-node breakdown popover ('antidote' | 'toxicity' | null).
  const [effectPopover, setEffectPopover] = useState(null);

  // Split the active pairs into the two pseudo nodes. Antidote node = the +HP
  // reversal pairs; Toxicity node = the -HP opposition/PK pairs PLUS the
  // masked-hypoglycemia caution (a hazard flag with no HP number).
  const pairGroups = useMemo(() => {
    const active = drugPairsQuery.data?.active_pairs || [];
    const antidotes = active.filter((p) => p.type === 'antidote');
    const toxicities = active.filter((p) => p.type === 'toxicity' || p.type === 'masked_hypo');
    const sum = (list) => list.reduce((t, p) => t + (Number(p.hp_delta) || 0), 0);
    return {
      antidote: { pairs: antidotes, total: sum(antidotes) },
      toxicity: { pairs: toxicities, total: sum(toxicities) },
    };
  }, [drugPairsQuery.data?.active_pairs]);

  const equippedKeystoneSlug = playerStateQuery.data?.equipped_keystone
    ? String(playerStateQuery.data.equipped_keystone).toLowerCase()
    : null;
  const keystoneDescriptor = equippedKeystoneSlug
    ? getKeystoneDescriptor(equippedKeystoneSlug)
    : null;

  // Nice display labels keyed by normalized drug_slug, sourced from the same
  // player-state node payloads the tree renders. Fallback = prettified slug.
  const labelBySlug = useMemo(() => {
    const m = new Map();
    (playerStateQuery.data?.nodes || []).forEach((n) => {
      const slug = String(n?.drug_slug || '').trim().toLowerCase();
      if (slug && n?.display_name) m.set(slug, n.display_name);
    });
    return m;
  }, [playerStateQuery.data?.nodes]);

  // Per-drug SKILL-TREE IDENTITY color, keyed by normalized slug. Built from the
  // SAME tree_definition the SkillTreeViewport renders (player-state carries it),
  // run through the SAME normalizer + the shared resolveNodeIdentityColor rule
  // (via buildDrugColorMap). So each equipped atom below paints in its tree
  // affinity color and stays in lockstep with the tree automatically — no
  // hand-maintained drug→color table to drift. (Edward 2026-06-09)
  const colorBySlug = useMemo(() => {
    const td = playerStateQuery.data?.tree_definition;
    if (!td) return new Map();
    const userProgress = playerStateQuery.data?.user_progress
      || { node_states: playerStateQuery.data?.node_states || {} };
    const graph = normalizeSkillTreeDefinition(td, userProgress);
    return buildDrugColorMap(graph, playerStateQuery.data?.equipped_keystone || null);
  }, [
    playerStateQuery.data?.tree_definition,
    playerStateQuery.data?.user_progress,
    playerStateQuery.data?.node_states,
    playerStateQuery.data?.equipped_keystone,
  ]);

  // Authoritative equipped drug slugs for the ACTIVE shelf (backend already
  // scopes equipped_drugs to the active shelf via get_active_slots — no
  // all-shelf leakage). Drop the keystone slug (rendered separately at keystone
  // scale) and de-dupe defensively.
  const equippedDrugSlugs = useMemo(() => {
    const raw = playerStateQuery.data?.equipped_drugs || [];
    const seen = new Set();
    const out = [];
    for (const s of raw) {
      const slug = String(s || '').trim().toLowerCase();
      if (!slug || slug === equippedKeystoneSlug || seen.has(slug)) continue;
      seen.add(slug);
      out.push(slug);
    }
    return out;
  }, [playerStateQuery.data?.equipped_drugs, equippedKeystoneSlug]);

  // Align the equipped keystone (the RIGHTMOST atom) horizontally with the
  // RIGHTMOST favorited hot-pick orb directly above it. The static CSS right:21px
  // gets this roughly right but is ~5px off (glyph/padding quirk); we instead
  // measure the live geometry so the keystone circle lands dead-center under the
  // rightmost orb. Both the hot-picks tray and this overlay are right-anchored
  // inside .deck-map-area; the rightmost orb is the orb closest to the right edge
  // (last in DOM order). The keystone's center sits a fixed inset inside the
  // overlay's right edge, so:
  //   overlay.right = (mapArea.right - rightmostOrbCenterX) - keystoneInset
  // We MEASURE the keystone circle's live inset from the SVG's right edge rather
  // than assume cellWidth/2 — the rendered inset carries small glyph/padding
  // quirks, and it's invariant to the overlay's own right offset (both move
  // together), so it stays stable across re-measures. Falls back to the CSS
  // default when nothing is pinned or no keystone is equipped. Edward 2026-06-09.
  const overlayRef = useRef(null);
  const [overlayRight, setOverlayRight] = useState(null);
  useLayoutEffect(() => {
    if (inline) return undefined; // in-flow variant — no dashboard-corner alignment
    let raf = 0;
    const measure = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const svg = overlayRef.current;
        const area = svg && (svg.offsetParent || svg.parentElement);
        const slots = document.querySelectorAll('.map-orb-hot-picks__slot');
        const rightmostSlot = slots.length ? slots[slots.length - 1] : null;
        const keystoneCircle = svg && svg.querySelector('.stoc-node--keystone circle');
        if (!svg || !area || !rightmostSlot || !keystoneCircle) { setOverlayRight(null); return; }
        const svgR = svg.getBoundingClientRect();
        const kc = keystoneCircle.getBoundingClientRect();
        const keystoneInset = svgR.right - (kc.left + kc.width / 2); // measured, exact
        const slot = rightmostSlot.getBoundingClientRect();
        const areaR = area.getBoundingClientRect();
        const rightmostOrbCx = slot.left + slot.width / 2;
        const right = (areaR.right - rightmostOrbCx) - keystoneInset;
        setOverlayRight(Number.isFinite(right) ? right : null);
      });
    };
    measure();
    window.addEventListener('resize', measure);
    // Re-measure when the hot-picks row mounts/unmounts (childList on the map
    // area) or the user pins/unpins an orb (childList on the hot-picks row), so
    // the keystone keeps tracking the live rightmost-orb column.
    const observers = [];
    if ('MutationObserver' in window) {
      const area = overlayRef.current?.offsetParent;
      const hp = document.querySelector('.map-orb-hot-picks');
      if (area) { const mo = new MutationObserver(measure); mo.observe(area, { childList: true }); observers.push(mo); }
      if (hp) { const mo = new MutationObserver(measure); mo.observe(hp, { childList: true }); observers.push(mo); }
    }
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', measure);
      observers.forEach((o) => o.disconnect());
    };
  });

  if (!keystoneDescriptor && !equippedDrugSlugs.length) return null;

  // Layout: [gold card 1] [gold card 2] ... [keystone (bigger, RIGHTMOST)]
  // The keystone is always the rightmost atom — the cluster is anchored
  // bottom-right of .deck-map-area so visually the keystone "owns" the
  // corner and drug cards extend leftward from it as they're equipped.
  const items = [];
  // Leading pseudo nodes: aggregate player-level Antidote / Toxicity effects from
  // the equipped drug pairs. They sit left of the real drug atoms (keystone stays
  // rightmost); click opens the stratified breakdown popover. Rendered only when
  // that kind of pair is actually active.
  if (pairGroups.antidote.pairs.length > 0) {
    items.push({
      kind: 'pseudo', pseudoType: 'antidote', id: 'pseudo__antidote',
      label: 'Antidote', radius: NODE_R_TOPIC, accent: '#22c55e',
      total: pairGroups.antidote.total,
    });
  }
  if (pairGroups.toxicity.pairs.length > 0) {
    items.push({
      kind: 'pseudo', pseudoType: 'toxicity', id: 'pseudo__toxicity',
      label: 'Toxicity', radius: NODE_R_TOPIC, accent: '#fb923c',
      total: pairGroups.toxicity.total,
    });
  }
  for (const slug of equippedDrugSlugs) {
    const label = labelBySlug.get(slug) || prettifySlug(slug);
    items.push({
      kind: 'card',
      id: slug.replace(/\s+/g, '-'),
      slug,
      label,
      radius: NODE_R_TOPIC,
      isEquipped: true,
      stmNode: {
        id: slug.replace(/\s+/g, '-'),
        label,
        payloadData: { drug_slug: slug },
        _stm: {
          status: 'equipped',
          payloadHandler: 'card_unlock',
          payloadData: { drug_slug: slug, card_name: label },
        },
      },
    });
  }
  if (keystoneDescriptor) {
    items.push({
      kind: 'keystone',
      id: `keystone__${keystoneDescriptor.id}`,
      label: keystoneDescriptor.genericName,
      radius: NODE_R_KEYSTONE,
      accent: keystoneDescriptor.accent,
      iconPath: getKeystoneIconPath(keystoneDescriptor.id),
      stmNode: {
        id: `keystone__${keystoneDescriptor.id}`,
        label: keystoneDescriptor.genericName,
        payloadData: { keystone_slug: keystoneDescriptor.id },
        _stm: {
          status: 'equipped',
          payloadHandler: 'keystone_marker',
          payloadData: { keystone_slug: keystoneDescriptor.id, card_name: keystoneDescriptor.genericName },
          description: `${keystoneDescriptor.abilityName}: ${keystoneDescriptor.drugClass}`,
        },
      },
    });
  }

  // Lay out cells in a single horizontal row. Cell height matches the
  // largest node so the row aligns vertically with whichever item is
  // present.
  const maxRadius = items.reduce((m, it) => Math.max(m, it.radius), 0);
  const overlayH = maxRadius * 2 + 4;
  let cursorX = 0;
  const placements = items.map((it) => {
    const cellW = cellWidthFor(it.radius);
    const cx = cursorX + cellW / 2;
    cursorX += cellW;
    return { ...it, cx, cy: overlayH / 2 };
  });
  const overlayW = cursorX;

  return (
    <>
      <svg
        ref={overlayRef}
        className={`stoc-overlay${inline ? ' stoc-overlay--inline' : ''}${!equippedKeystoneSlug ? ' stoc-overlay--no-keystone' : ''}${spotlight ? ' stoc-overlay--spotlight' : ''}`}
        width={overlayW}
        height={overlayH}
        viewBox={`0 0 ${overlayW} ${overlayH}`}
        aria-label="Equipment loadout"
        style={!inline && overlayRight != null ? { right: `${overlayRight}px` } : undefined}
      >
        {placements.map((it) => {
          const isPseudo = it.kind === 'pseudo';
          const isKeystone = it.kind === 'keystone';
          const isActive = isPseudo
            ? effectPopover === it.pseudoType
            : currentNodeId === it.id;
          // Non-keystone cards paint in their resolved skill-tree identity color
          // (gold / keystone-affinity / warfarin-purple), falling back to the
          // canonical equipped-green only when the slug isn't in the tree graph.
          // Pseudo nodes carry their own semantic accent (antidote green /
          // toxicity orange).
          const cardAccent = isPseudo
            ? it.accent
            : isKeystone
              ? it.accent
              : (colorBySlug.get(normalizeOwnableSlug(it.slug)) || GREEN);
          const totalLabel = isPseudo
            ? `${it.label} (${it.total > 0 ? '+' : ''}${it.total} max HP)`
            : it.label;
          return (
            <g
              key={it.id}
              className={`stoc-node${isActive ? ' stoc-node--active' : ''}${isKeystone ? ' stoc-node--keystone' : ''}${isPseudo ? ` stoc-node--pseudo stoc-node--pseudo-${it.pseudoType}` : ''}`}
              transform={`translate(${it.cx}, ${it.cy})`}
              onClick={isPseudo
                ? () => setEffectPopover(it.pseudoType)
                : (e) => openNodeToast(it.stmNode, e, { readOnly: true })}
              role="button"
              aria-label={isPseudo
                ? `${it.label} effects`
                : `${isKeystone ? 'Keystone' : 'Owned card'}: ${it.label}`}
            >
              <title>{totalLabel}</title>
              {isActive && (
                <circle
                  r={it.radius + 5}
                  fill="none"
                  stroke={cardAccent}
                  strokeWidth={1.2}
                  strokeOpacity={0.75}
                  style={{ pointerEvents: 'none' }}
                />
              )}
              {isPseudo ? (
                // Pseudo atom: dashed ring (signals "not a real drug card") +
                // a semantic symbol. Antidote = plus (reversal), Toxicity =
                // exclamation (hazard).
                <>
                  <circle
                    r={it.radius}
                    fill={`${it.accent}2e`}
                    stroke={it.accent}
                    strokeWidth={1.5}
                    strokeDasharray="3 2.5"
                  />
                  {it.pseudoType === 'antidote' ? (
                    <path
                      d={`M0,${-it.radius * 0.42} L0,${it.radius * 0.42} M${-it.radius * 0.42},0 L${it.radius * 0.42},0`}
                      stroke={it.accent}
                      strokeWidth={2.2}
                      strokeLinecap="round"
                      style={{ pointerEvents: 'none' }}
                    />
                  ) : (
                    <g style={{ pointerEvents: 'none' }}>
                      <path
                        d={`M0,${-it.radius * 0.45} L0,${it.radius * 0.18}`}
                        stroke={it.accent}
                        strokeWidth={2.2}
                        strokeLinecap="round"
                      />
                      <circle cx={0} cy={it.radius * 0.45} r={1.35} fill={it.accent} />
                    </g>
                  )}
                </>
              ) : (
                /* The node atom (stroked circle + glyph) is the shared
                   SkillTreeNodeGlyph — the SAME component the tooltip header
                   renders, so the hotbar atom and the tooltip can never diverge
                   into a lookalike recreation. (Edward 2026-06-26) */
                <SkillTreeNodeGlyph
                  isKeystone={isKeystone}
                  slug={it.slug}
                  iconPath={it.iconPath}
                  accent={cardAccent}
                  radius={it.radius}
                  equipped={it.isEquipped}
                />
              )}
            </g>
          );
        })}
      </svg>
      {effectPopover && pairGroups[effectPopover] && (
        <DrugPairEffectPopover
          kind={effectPopover}
          total={pairGroups[effectPopover].total}
          pairs={pairGroups[effectPopover].pairs}
          onClose={() => setEffectPopover(null)}
        />
      )}
    </>
  );
}
